import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  Stack,
  Text,
} from "@chakra-ui/react";
import { differenceInYears } from "date-fns";
import { Field, Formik } from "formik";
import { observer } from "mobx-react-lite";
import { getSnapshot } from "mobx-state-tree";
import React, { FC, useEffect } from "react";
import { boolean, object, string } from "yup";
import { FFormContext } from "../primitives/forms/FormField";
import { FFormField } from "../primitives/landing-pages";
import { IIdentityStore, IPersonalInfoStore } from "../stores";
import { capFirstLetter, scrollToId, scrollToTop } from "../utils/functions";
import _personalInfoData from "./formData/personalInfo.json";
import { ImageInput } from "./VerificationIdentity";

export const tutorFormInputProps = {
  focusBorderColor: "blue.500",
  colorScheme: "blue",
  fontSize: "16px",
  size: "lg",
  borderColor: "neutral.500",
};

export function getValidationSchema({ validationData }: any, minCode: number) {
  let validationSchema = object().shape({
    firstName: string().trim().required(validationData.firstName.required),
    lastName: string().trim().required(validationData.lastName.required),
    email: string()
      .required(validationData.email.required)
      .email(validationData.email.valid),
    // .test("email", validationData.email.test, function (value) {
    //   if (value) {
    //     return !wrongEmailFormats.some((v) => value.includes(v));
    //   }
    //   return false;
    // }),
    subscription: boolean()
      .required()
      .oneOf([true], validationData.subscription.required),
    dateOfBirth: string()
      .matches(
        /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/,
        validationData.dateOfBirth.matches
      )
      .required(validationData.dateOfBirth.required)
      .test(
        "dateOfBirth",
        validationData.dateOfBirth.test,
        function (value: any) {
          if (value) {
            const yearDifference = differenceInYears(
              new Date(),
              new Date(value)
            );
            return yearDifference >= 18;
          }
          return false;
        }
      ),
    gender: string().required(validationData.gender.required),
    phone: string()
      .min(minCode, validationData.phone.required)
      .required(validationData.phone.required)
      .required(validationData.phone.required),
    whatsappNumber: string()
      .min(minCode, validationData.whatsappNumber.required)
      .required(validationData.whatsappNumber.required),
    nationality: string().required(validationData.nationality.required),
    // primaryLanguage: string().required(validationData.primaryLanguage.required),
    // medium: string().required(validationData.medium.required),
    profilePic: string().required("Profile picture is required"),
  });
  return validationSchema;
}
function generateYearOptions(minAge = 13, maxAge = 60) {
  let currentYear = new Date().getFullYear();
  let startYear = currentYear - maxAge;
  let endYear = currentYear - minAge;
  let options: any = [];
  for (let i = startYear; i < endYear + 1; i++) {
    options = [...options, i];
  }

  return options.map((item: any) => ({ label: item, value: item.toString() }));
}
const RawPersonalInfoForm = observer(
  (props: {
    store: IPersonalInfoStore;
    imageStore: IIdentityStore;
    onSubmit: (...params: any[]) => void;
    loading: boolean;
    innerRef?: any;
    sources?: string[];
    initialValues?: any;
    nationalities?: any[];
    buttonProps?: any;
    onValidateStep?: any;
    validationSchema: any;
    afterUpload?: any;
    imagePlaceholder?: string;
    heading: {
      displayHeader?: boolean;
      formTitle: {
        header: string;
        subHeader: string;
        errorMessage: string;
      };
      buttonText: {
        loadingText: string;
        saveAndContinue: string;
      };
    };
    onImageUpload: any;
    idVerified?: boolean;
  }) => {
    const {
      idVerified,
      store,
      imageStore,
      onSubmit,
      loading,
      innerRef,
      buttonProps = {},
      initialValues,
      onValidateStep,
      validationSchema,
      afterUpload,
      nationalities = [],
      heading,
      onImageUpload,
      imagePlaceholder,
    } = props;
    React.useEffect(() => scrollToTop(), []);

    const [showFormError, setShowFormError] = React.useState(false);

    // const onSubmitClick = (errors = {}) => {
    //   if (errors) {
    //     console.log("errors", errors);

    //     if (Object.values(errors).length) {
    //       setShowFormError(true);
    //       scrollToId("form-header-section");
    //     } else {
    //       setShowFormError(false);
    //     }
    //   }
    // };
    const _handleSubmit = async (values: any, formikHelpers: any) => {
      formikHelpers.setSubmitting(true);
      try {
        let firstName = capFirstLetter(values.firstName.toLowerCase());
        let lastName = capFirstLetter(values.lastName.toLowerCase());
        let email = values.email.toLowerCase();
        let newValues = { ...values, firstName, lastName, email };
        if (onValidateStep) {
          await onValidateStep(newValues);
        }
        let res = await onSubmit(newValues);
        formikHelpers.setSubmitting(false);
        return res;
      } catch (error: any) {
        formikHelpers.setSubmitting(false);
        if (error?.formErrors) {
          formikHelpers.setErrors(error.formErrors);
        }
      }
    };
    return (
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        ref={innerRef}
        onSubmit={_handleSubmit}
      >
        {({ handleSubmit, errors, isSubmitting, validateForm }) => {
          useEffect(() => {
            if (Object.keys(errors).length > 0) {
              setShowFormError(true);
            } else {
              setShowFormError(false);
            }
          }, [errors]);

          const onSubmitClick = async () => {
            if (errors) {
              const validationErrors = await validateForm();
              if (Object.keys(validationErrors).length > 0) {
                scrollToId("form-header-section");
              } else {
                handleSubmit();
              }
            }
          };
          return (
            <form
              // onSubmit={(value) => {
              //   handleSubmit(value);
              //   onSubmitClick(errors);
              // }}
              onSubmit={(e) => {
                e.preventDefault();
                onSubmitClick();
              }}
            >
              <Stack
                spacing={6}
                id="form-header-section"
                className="personal-info-form"
              >
                {heading.displayHeader && (
                  <Stack spacing={4} mb={4}>
                    <Text
                      fontWeight="bold"
                      lineHeight="shorter"
                      fontSize={["3xl", "4xl"]}
                    >
                      {heading.formTitle.header}
                    </Text>
                    <Text color="gray.700">{heading.formTitle.subHeader}</Text>
                    {showFormError && (
                      <Box pt={2}>
                        <Alert rounded="lg" status="error">
                          <AlertIcon />
                          {heading.formTitle.errorMessage}
                        </Alert>
                      </Box>
                    )}
                  </Stack>
                )}

                <Flex
                  direction={["column", "row"]}
                  justifyContent="space-between"
                >
                  <FFormField name="firstName" isDisabled={idVerified} />

                  <Box width={6} height={6} />

                  <FFormField name="lastName" isDisabled={idVerified} />
                </Flex>

                <FFormField name="email" type="email" />

                <FFormField name="subscription" type="checkbox" hideLabel />

                <Box width={{ base: "full", md: "md" }}>
                  <FFormField
                    name="dateOfBirth"
                    type="date"
                    isDisabled={idVerified}
                    years={generateYearOptions()}
                  />
                </Box>

                <Flex
                  direction={["column", "row"]}
                  justifyContent="space-between"
                >
                  <FFormField
                    name="phone"
                    type="phone"
                    country_code={store.country_code.toLowerCase()}
                  />

                  <Box width={12} height={6} />
                  <FFormField
                    name="whatsappNumber"
                    type="phone"
                    country_code={store.country_code.toLowerCase()}
                  />
                </Flex>

                <Flex
                  direction={["column", "row"]}
                  justifyContent="space-between"
                >
                  <FFormField
                    name="gender"
                    type="gender"
                    isDisabled={idVerified}
                  />
                  <Box width={12} height={6} />
                  <FFormField
                    name="nationality"
                    type="select"
                    isDisabled={idVerified}
                    key={`${nationalities.length > 0}`}
                    options={nationalities}
                  />
                  {/* <FFormField
                  name="medium"
                  type="select"
                  options={mediums}
                  key={`${mediums.length > 0}`}
                /> */}
                </Flex>

                <Box py={{ base: 6, md: 8 }}>
                  <Divider />
                </Box>

                <Box>
                  <Stack id="form-header-section" mb={4}>
                    <Text fontWeight="bold" fontSize={["3xl", "4xl"]}>
                      Add profile photo
                    </Text>
                    <Text color="gray.700">
                      Upload your best profile photo. Clients will see this when
                      selecting tutors. Tutors with professional, high-quality
                      photos can get 3x more bookings than others.
                    </Text>
                  </Stack>

                  <Field name="profilePic">
                    {({ field, form }: any) => {
                      const fieldValue = field.value || "";
                      return (
                        <ImageInput
                          value={fieldValue}
                          placeholder="Upload Image Placeholder"
                          label="Upload Image"
                          loading={imageStore.loading}
                          hasError={
                            imageStore.goodQuality && imageStore.has_face
                          }
                          imagePlaceholder={imagePlaceholder}
                          onChange={async (image) => {
                            const { profilePhoto, profilePhotoId } =
                              await onImageUpload(image);
                            form.setFieldValue(field.name, profilePhoto);
                            form.setFieldValue(
                              "profilePhotoId",
                              profilePhotoId
                            );
                            return profilePhoto;
                          }}
                        />
                      );
                    }}
                  </Field>
                </Box>

                {/* <Flex direction={["column", "row"]} justify="space-between">
                <FFormField
                  name="nationality"
                  type="select"
                  key={`${nationalities.length > 0}`}
                  options={nationalities}
                />

                <Box width={12} height={6} />
                <FFormField
                  name="primaryLanguage"
                  type="select"
                  key={`${primaryLanguages.length > 0}`}
                  options={primaryLanguages}
                />
              </Flex> */}

                <Box pt={[4, 5]} width="full">
                  <Button
                    type="submit"
                    size="lg"
                    isFullWidth
                    colorScheme="blue"
                    isLoading={loading}
                    loadingText={heading.buttonText.loadingText}
                    {...buttonProps}
                  >
                    {heading.buttonText.saveAndContinue}
                  </Button>
                </Box>
              </Stack>
            </form>
          );
        }}
      </Formik>
    );
  }
);

export type PersonalInfoProps = {
  store: IPersonalInfoStore;
  imageStore: IIdentityStore;
  formHeader?: any;
  formSummary?: any[];
  loading: boolean;
  countries: string[];
  displayHeader?: boolean;
  // label: string;
  minCode: number;
  imagePlaceholder?: string;
  // lockedDescription: string;
  [key: string]: any;
  onSubmit: (params?: any) => any;
  onImageUpload: (params: any) => Promise<any>;
  idVerified?: boolean;
};

const PersonalInfoForm: FC<PersonalInfoProps> = ({
  store,
  imageStore,
  onSubmit,
  loading,
  displayHeader = true,
  countries,
  minCode,
  idVerified,
  ...rest
}) => {
  let { formTitle, buttonText } = _personalInfoData;
  const nationalities = countries.map((country: string) => {
    return { value: country, label: country };
  });

  return (
    <FFormContext.Provider
      value={
        {
          placeholders: _personalInfoData.placeholders,
          genders: _personalInfoData.genders,
        } as any
      }
    >
      <RawPersonalInfoForm
        key={store.firstName}
        store={store}
        heading={{ formTitle, buttonText, displayHeader }}
        initialValues={{
          ...store.formValues,
          profilePic: imageStore.profilePhoto,
          profilePhotoId: imageStore.profilePhotoId,
        }}
        loading={loading}
        {...{ nationalities, idVerified }}
        onSubmit={onSubmit}
        imageStore={imageStore}
        validationSchema={getValidationSchema(_personalInfoData, minCode)}
        {...rest}
      />
    </FFormContext.Provider>
  );
};

export default observer(PersonalInfoForm);
